import type { QuestionInputObjectValue, QuestionInputValue } from '/index';
import type { QuestionSystemField } from '../useQuestionSystem';

export type MappedValue = string | number | boolean | null;
export type MappedFieldValue = QuestionInputObjectValue | QuestionInputValue | false | undefined;

function isNamedValue(value: MappedFieldValue): value is QuestionInputObjectValue {
  return typeof value === 'object' && value !== null && 'name' in value;
}

function isMappedValue(value: MappedFieldValue | MappedValue): value is MappedValue {
  return value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

function isQuestionInputObjectValue(value: MappedFieldValue): value is QuestionInputObjectValue {
  return typeof value === 'object' && value !== null && 'name' in value && typeof value.name === 'string';
}

export function getMappedValue(value: MappedFieldValue): MappedValue {
  if (isNamedValue(value) && isMappedValue(value.name)) {
    return value.name;
  }
  return isMappedValue(value) ? value : null;
}

export function getCallbackValue(value: MappedFieldValue): QuestionInputObjectValue | QuestionInputValue | undefined {
  if (isQuestionInputObjectValue(value)) {
    return value;
  }
  return isMappedValue(value) ? value : undefined;
}

export function getControlValue(value: MappedValue): string | number | undefined {
  if (value === null) {
    return undefined;
  }
  return typeof value === 'boolean' ? String(value) : value;
}

export function getInputMode(inputMode: QuestionSystemField['inputMode']): 'text' | 'numeric' | 'decimal' | undefined {
  return inputMode === 'number' ? 'numeric' : inputMode;
}

export function getStringValue(value: QuestionInputValue | undefined | false): string {
  if (value === null || value === undefined || value === false) {
    return '';
  }
  return String(value);
}

export function getTranslatedOrLiteralText(value: string, t: (key: string) => string): string {
  const translatedValue = t(value);
  if (translatedValue !== '___' || value.includes(':')) {
    return translatedValue;
  }
  return value;
}

export function getTranslatedDisplayName(
  displayName: string | false | undefined,
  displayNameKey: string | undefined,
  t: (key: string) => string,
  fallbackName?: null | string,
): string {
  if (displayNameKey && displayNameKey.trim() !== '') {
    const translatedValue = t(displayNameKey);
    if (translatedValue !== '___') {
      return translatedValue;
    }
    if (!displayNameKey.includes(':')) {
      return displayNameKey;
    }
    if (typeof displayName === 'string' && displayName.trim() !== '') {
      return displayName;
    }
    return translatedValue;
  }
  if (typeof displayName === 'string' && displayName.trim() !== '') {
    return displayName;
  }
  return fallbackName ?? '';
}
